import { exibirNomeProduto, exibirNomeProposta } from '../../shared/constantes/nomenclatura';
import 'bootstrap/dist/css/bootstrap.css';
import { useState, useEffect } from 'react';
import Spinner from '../../shared/spinner'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faLayerGroup, faSave, faListUl } from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../shared/customAlert'; 
import ComissaoService from '../../services/comissaoService'; 
import { format } from 'date-fns';

// --- 1. DEFINIÇÃO DOS ENUMS QUE VÊM DA API ---
// Baseado no seu JSON: "CARTAO_BENEFICIO", "CARTAO_CREDITO_CONSIGNADO"
const TIPOS_PRODUTO = [
    { value: 'CARTAO_BENEFICIO', label: exibirNomeProduto('Cartão Benefício') },
    { value: 'CARTAO_CREDITO_CONSIGNADO', label: exibirNomeProduto('Cartão Consignado') }
];

// Baseado no seu JSON: "REFINANCIAMENTO", "PORTABILIDADE", etc.
const TIPOS_OPERACAO = [
    { value: 'NOVO', label: exibirNomeProposta('Saque Novo / Convencional') }, // Corrigido de 'SAQUE' para 'NOVO'
    { value: 'SAQUE_COMPLEMENTAR', label: 'Saque Complementar' },
    { value: 'REFINANCIAMENTO', label: exibirNomeProposta('Refinanciamento') },
    { value: 'PORTABILIDADE', label: 'Portabilidade' },
    { value: 'PORTABILIDADE_COM_REFINANCIAMENTO', label: 'Portabilidade + Refin' },
    { value: 'CARTAO_SACADO', label: exibirNomeProposta('Cartão Sacado') },
    { value: 'CARTAO_SEM_SAQUE', label: exibirNomeProposta('Cartão sem Saque') }
];

// Interface local para tipagem correta baseada no JSON da API
interface ComissaoPerfilDTO {
    id?: number;
    nome: string; // JSON: "nome"
    tipoProduto: string; // JSON: "tipoProduto" (String)
    operacoesHabilitadas: string[]; // JSON: "operacoesHabilitadas" (Array de Strings)
    dataCriacao?: string;
    usuarioCriacao?: string;
}

function TipoComissaoComponent() {
  
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [listaTipos, setListaTipos] = useState<ComissaoPerfilDTO[]>([]);
  const [showModal, setShowModal] = useState(false);
  
  // Inicialização ajustada para Strings
  const [formTipo, setFormTipo] = useState<ComissaoPerfilDTO>({
    nome: '',
    tipoProduto: 'CARTAO_BENEFICIO', // Valor default válido
    operacoesHabilitadas: []   
  });

  const comissaoService = new ComissaoService();

  useEffect(() => {
    listarTipos();
  }, []);

  const listarTipos = async () => {
    try {
      setLoading(true);
      const response = await comissaoService.listarPerfis();
      // O JSON que você mostrou é um array direto, ou vem dentro de response.data?
      // Assumindo response.data conforme padrão axios
      setListaTipos(response.data || []);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      console.error(err);
      setAlert({ message: 'Erro ao carregar tipos de comissão.', type: 'error' });
    }
  };

  const abrirModalNovo = () => {
    setFormTipo({
      nome: '',
      tipoProduto: 'CARTAO_BENEFICIO',
      operacoesHabilitadas: []
    });
    setShowModal(true);
  };

  const abrirModalEditar = (item: ComissaoPerfilDTO) => {
    setFormTipo({ 
        ...item,
        // Garante array vazio caso venha null
        operacoesHabilitadas: item.operacoesHabilitadas || [] 
    });
    setShowModal(true);
  };

  // --- LÓGICA DE SELEÇÃO ATUALIZADA PARA STRINGS ---
  const toggleOperacao = (valorOperacao: string) => {
    setFormTipo(prev => {
        const listaAtual = prev.operacoesHabilitadas || [];
        const jaExiste = listaAtual.includes(valorOperacao);
        let novasOperacoes;
        
        if (jaExiste) {
            novasOperacoes = listaAtual.filter(op => op !== valorOperacao);
        } else {
            novasOperacoes = [...listaAtual, valorOperacao];
        }
        
        return { ...prev, operacoesHabilitadas: novasOperacoes };
    });
  };

  const salvarTipo = async () => {
    // Validação usando o campo correto 'nome'
    if (!formTipo.nome) return setAlert({ message: 'Informe o nome do Tipo de Comissão.', type: 'warning' });
    
    if (!formTipo.operacoesHabilitadas || formTipo.operacoesHabilitadas.length === 0) {
        return setAlert({ message: 'Selecione ao menos uma operação.', type: 'warning' });
    }

    try {
      setLoading(true);
      await comissaoService.salvarPerfil(formTipo);
      setAlert({ message: 'Tipo de comissão salvo com sucesso!', type: 'success' });
      setShowModal(false);
      listarTipos();
    } catch (err: any) {
      setLoading(false);
      setAlert({ message: 'Erro ao salvar Tipo de Comissão.', type: 'error' });
    }
  };

  // Helpers de Exibição
  const getDescricaoProduto = (valorBackend: string) => {
      const encontrado = TIPOS_PRODUTO.find(p => p.value === valorBackend);
      return encontrado ? encontrado.label : exibirNomeProduto(valorBackend);
  };
  
  const getDescricaoOperacoes = (lista: string[]) => {
      if (!lista || lista.length === 0) return '-';
      return lista.map(opKey => {
          const op = TIPOS_OPERACAO.find(o => o.value === opKey);
          return op ? op.label : exibirNomeProposta(opKey);
      }).join(', ');
  };

  const formatDate = (dateStr?: string) => {
      if(!dateStr) return '-';
      try { return format(new Date(dateStr), 'dd/MM/yyyy HH:mm'); } catch { return dateStr; }
  };

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-body">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5>Parâmetros &gt; Tipo de Comissão</h5>
                <button className="btn btn-success" onClick={abrirModalNovo}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} /> Novo Tipo
                </button>
              </div>

              <div className="table-responsive mt-3">
                <table className="table table-bordered table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th className="text-center" style={{width: '60px'}}>ID</th>
                      <th>Nome</th>
                      <th>Tipo Produto</th>
                      <th>Operações Permitidas</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listaTipos.length > 0 ? (
                        listaTipos.map((item) => (
                          <tr key={item.id}>
                            <td className="text-center">{item.id}</td>
                            <td className="fw-bold">{item.nome}</td>
                            <td>
                                <span className="badge bg-primary">
                                    {getDescricaoProduto(item.tipoProduto)}
                                </span>
                            </td>
                            <td>
                                <small className="text-muted">{getDescricaoOperacoes(item.operacoesHabilitadas)}</small>
                            </td>
                            <td className="text-center">
                                <button className="btn btn-sm btn-outline-primary" onClick={() => abrirModalEditar(item)}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                            </td>
                          </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={5} className="text-center">Nenhum registro encontrado.</td>
                        </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {showModal && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {formTipo.id ? 'Editar' : 'Novo'} Tipo de Comissão
                            </h5>
                            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                        </div>
                        <div className="modal-body">
                            
                            <div className="row mb-3">
                                <div className="col-md-8 form-group">
                                    <label>Nome <span className="text-danger">*</span></label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={formTipo.nome || ''}
                                        onChange={e => setFormTipo({...formTipo, nome: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-4 form-group">
                                    <label>Tipo Produto <span className="text-danger">*</span></label>
                                    <select 
                                        className="form-control"
                                        value={formTipo.tipoProduto}
                                        onChange={e => setFormTipo({...formTipo, tipoProduto: e.target.value})}
                                    >
                                        {TIPOS_PRODUTO.map(tp => (
                                            <option key={tp.value} value={tp.value}>{tp.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="card bg-light">
                                <div className="card-header py-1">
                                    <small className="fw-bold"><FontAwesomeIcon icon={faListUl} /> Tipos de Proposta</small>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        {TIPOS_OPERACAO.map(op => (
                                            <div className="col-md-6 mb-2" key={op.value}>
                                                <div className="form-check">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="checkbox" 
                                                        id={`op-${op.value}`}
                                                        checked={formTipo.operacoesHabilitadas?.includes(op.value) || false}
                                                        onChange={() => toggleOperacao(op.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`op-${op.value}`}>
                                                        {op.label}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button type="button" className="btn btn-success" onClick={salvarTipo}>
                                <FontAwesomeIcon icon={faSave} style={{ marginRight: '5px' }} /> Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

      </div>
    </>
  );
}

export default TipoComissaoComponent;